import React, { useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { Tag } from 'primereact/tag'
import { confirmDialog } from 'primereact/confirmdialog'
import { Button } from 'primereact/button'

import { useChargePointContext } from '../../../common/context/ChargePointContext'
import OcppSettings from '../../../common/features/ocpp-settings/OcppSettings'
import useOcppFormValidation, {
    IOcppFormInput,
    ocppChargerDefaultValues,
} from '../../../common/hooks/useOcppFormValidation'
import { useChargePointUpdateMutation, ChargePoint } from '../../../common/redux/api/web-api'
import { mapOcppChargepoint } from '../../../common/utils/objectMappers'
import useText from '../../../common/hooks/useText'
import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import isOnline from '../../../lib/isOnline'
import Text from '../../../ui/Text'
import { useToast } from '../../../common/uikit/ToastProvider'

export type OcppElectricalSettingsProps = {
    chargePoint: ChargePoint
}

export function OcppElectricalSettings({ chargePoint }: OcppElectricalSettingsProps): React.ReactElement {
    const text = useText()
    const [updateChargePoint, { isLoading: chargePointUpdating }] = useChargePointUpdateMutation()
    const { autoRefresh, setAutoRefresh } = useChargePointContext()
    const ocppValidationMethods = useOcppFormValidation(chargePoint)
    const PartnerOcppEnabledCharger = isPartnerDomain && !chargePoint?.ocppEnabled && chargePoint?.isOcpp
    const PartnerCapiCharger = isPartnerDomain && !chargePoint?.isOcpp
    const {
        reset,
        formState: { isValid, dirtyFields },
    } = ocppValidationMethods

    useEffect(() => {
        reset(ocppChargerDefaultValues(chargePoint), {
            keepDirtyValues: true,
            keepDirty: true,
            keepErrors: true,
            keepIsValid: true,
        })
    }, [chargePoint, reset])

    const toast = useToast()

    const saveChargePoint = async (formValues: IOcppFormInput) => {
        try {
            await updateChargePoint({
                chargePoint: mapOcppChargepoint(chargePoint, formValues),
            })
                .unwrap()

                .then(() => {
                    toast.notifySuccess(text('msgChargerWasUpdated'))
                })
            setAutoRefresh(true)
        } catch (exception) {
            toast.notifyError(text('msgChargePointNotUpdated'))
        } finally {
            reset(formValues)
        }
    }
    const unlockCharger = () => {
        confirmDialog({
            message: <Text code="msgUnlockingInstallationSettings" />,
            header: text('lblUnlockingInstallationSettings'),
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            accept: () => setAutoRefresh(false),
        })
    }

    const getSettingsDisabledReason = () => {
        if (isAdminDomain) {
            return text('msgChargerElectricalSettingsCanBeModifiedByOperator')
        } else if (isOnline(chargePoint) && autoRefresh && chargePoint?.isOcpp && !chargePoint.ocppEnabled) {
            return text('msgInstallationRequiresUnlockCommand')
        } else if (!isOnline(chargePoint) && chargePoint?.isOcpp) {
            return text('msgChargerIsOfflineCurrentSettings')
        }
    }

    const disabledReason = getSettingsDisabledReason()

    return (
        <div data-testid="details">
            <div className="flex justify-content-between mb-2">
                {isOnline(chargePoint) && (isSupportDomain || PartnerOcppEnabledCharger || PartnerCapiCharger) && (
                    <>
                        <Button
                            onClick={() => {
                                if (autoRefresh) {
                                    unlockCharger()
                                } else {
                                    setAutoRefresh(true)
                                }
                            }}
                            className="flex gap-2"
                            label={autoRefresh ? text('lblUnlock') : text('lblUnlockedSettings')}
                            disabled={chargePoint.isOcpp && !isOnline(chargePoint)}
                            icon={
                                autoRefresh ? <i className="pi pi-lock"></i> : <i className="pi pi-lock-open ml-1"></i>
                            }
                        ></Button>
                        <div>
                            <Button
                                data-testid="ocpp-save"
                                label={text('btnSave')}
                                onClick={chargePoint && ocppValidationMethods.handleSubmit(saveChargePoint)}
                                disabled={chargePointUpdating || !(Object.keys(dirtyFields).length > 0) || !isValid}
                                loading={chargePointUpdating}
                            />
                        </div>
                    </>
                )}
            </div>
            <div className="mb-2">
                {disabledReason && (
                    <Tag
                        className="text-base"
                        severity="warning"
                        value={disabledReason}
                        icon={<i className="pi pi-info-circle mr-2"></i>}
                    ></Tag>
                )}
            </div>

            <FormProvider {...ocppValidationMethods}>
                <OcppSettings chargePoint={chargePoint} />
            </FormProvider>
        </div>
    )
}
